import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { CalendarEvent } from '@/lib/absences';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/date';
import { Link, useNavigate } from '@tanstack/react-router';
import { isWeekend, isToday, startOfDay, getDate } from 'date-fns';
import { ScheduleBar } from './schedule-bar';
import { assignBarLanes, rowHeightForLanes } from './schedule-lanes';
import { useCallback, useState } from 'react';

interface EmployeeData {
  id: string;
  name: string;
  avatar: string | null;
  position: string | null;
}

interface ScheduleRowProps {
  employee: EmployeeData;
  events: CalendarEvent[];
  days: Date[];
  searchQuery: string;
  onHoverDay: (day: number | null) => void;
  hoveredDay: number | null;
}

export function ScheduleRow({
  employee,
  events,
  days,
  searchQuery,
  onHoverDay,
  hoveredDay,
}: ScheduleRowProps) {
  const navigate = useNavigate();
  const [isRowHovered, setIsRowHovered] = useState(false);

  const initials = employee.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2);

  const renderName = useCallback(() => {
    if (!searchQuery) return employee.name;
    const idx = employee.name.toLowerCase().indexOf(searchQuery.toLowerCase());
    if (idx === -1) return employee.name;
    const before = employee.name.slice(0, idx);
    const match = employee.name.slice(idx, idx + searchQuery.length);
    const after = employee.name.slice(idx + searchQuery.length);
    return (
      <>
        {before}
        <mark className="rounded-sm bg-emerald-500/30 px-0.5 text-emerald-400">{match}</mark>
        {after}
      </>
    );
  }, [employee.name, searchQuery]);

  const bars = events.filter((e) => e.type !== 'birthday').map((event) => {
    const eventStart = startOfDay(parseDateOnly(event.startsOn));
    const eventEnd = startOfDay(parseDateOnly(event.endsOn));
    const monthStart = startOfDay(days[0]);
    const monthEnd = startOfDay(days[days.length - 1]);

    const visibleStart = eventStart < monthStart ? monthStart : eventStart;
    const visibleEnd = eventEnd > monthEnd ? monthEnd : eventEnd;

    const startDay = getDate(visibleStart);
    const spanDays = getDate(visibleEnd) - startDay + 1;

    return { event, startDay, spanDays };
  });

  // HW-701: stack overlapping bars on separate lanes and grow the row to fit,
  // so a pending request sharing a start day with an approved one is no longer
  // hidden by the single-bar-per-cell lookup.
  const { lanes, laneCount } = assignBarLanes(bars);
  const barsWithLane = bars.map((bar, i) => ({ ...bar, lane: lanes[i] }));
  const rowHeight = rowHeightForLanes(laneCount);

  const birthdays = events.filter((e) => e.type === 'birthday');

  return (
    <div
      className="contents"
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
    >
      <div
        className={cn(
          'sticky left-0 z-[5] flex cursor-pointer items-center gap-2.5 border-b border-border bg-background px-4 transition-colors hover:bg-muted/50',
          isRowHovered && 'bg-muted/30'
        )}
        style={{ minHeight: rowHeight }}
        onClick={() => navigate({ to: '/employees/$id', params: { id: employee.id } })}
      >
        <Link
          to="/employees/$id"
          params={{ id: employee.id }}
          onClick={(e) => e.stopPropagation()}
          className="flex min-w-0 items-center gap-2.5"
        >
          <Avatar className="h-[30px] w-[30px]">
            <AvatarImage src={employee.avatar || undefined} />
            <AvatarFallback className="text-[11px] font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium">{renderName()}</div>
            {employee.position && (
              <div className="truncate text-[10px] text-muted-foreground">{employee.position}</div>
            )}
          </div>
        </Link>
      </div>

      {days.map((day, dayIdx) => {
        const dayNum = getDate(day);
        const weekend = isWeekend(day);
        const today = isToday(day);
        const isColHovered = hoveredDay === dayNum;

        const barsForDay = barsWithLane.filter((b) => b.startDay === dayNum);
        const birthdayForDay = birthdays.find(
          (b) => getDate(parseDateOnly(b.startsOn)) === dayNum
        );

        return (
          <div
            key={dayIdx}
            className={cn(
              'relative border-b border-border border-l border-l-border/30 transition-colors',
              weekend && 'bg-muted/60',
              today && 'bg-emerald-500/[0.04]',
              isRowHovered && 'bg-emerald-500/[0.02]',
              isColHovered && 'bg-emerald-500/[0.03]',
              isRowHovered && isColHovered && 'bg-emerald-500/[0.07] ring-1 ring-inset ring-emerald-500/20'
            )}
            style={{ minHeight: rowHeight }}
            onMouseEnter={() => onHoverDay(dayNum)}
            onMouseLeave={() => onHoverDay(null)}
          >
            {barsForDay.map((bar) => (
              <ScheduleBar
                key={bar.event.id}
                event={bar.event}
                spanDays={bar.spanDays}
                isPending={bar.event.status === 'pending'}
                lane={bar.lane}
              />
            ))}
            {birthdayForDay && (
              <div className="absolute left-1/2 top-1/2 z-[3] flex h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-pink-500/20 text-xs">
                🎂
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
